import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  notFound,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import { useMemo, useState } from "react"
import * as z from "zod"

import type {
  ExtendedClient,
  ExtendedResourceInvoice,
} from "@repo/supabase/database-types"
import { If } from "@repo/ui/components/ui/if"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { ensureColumnFilter } from "~/lib/utils/datatable-query-builder"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getExtendedClientsQueryOptions } from "~/modules/clients/clients.queries"
import { ClientsSearchList } from "~/modules/clients/components/clients-search-list"
import { ExportInvoicesDialog } from "~/modules/resources/components/export-invoices-dialog"
import {
  type Pagination,
  ResourceInvoicesTable,
} from "~/modules/resources/components/resource-invoices-table"
import { getExtendedResourceInvoicesQueryOptions } from "~/modules/resources/resource-invoices.queries"
import { ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES } from "~/modules/resources/resources.constants"
import { createRolePermissionMethods } from "~/modules/roles-and-permissions/role-permission-methods"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "resource-invoices-route",
})

const searchSchema = dataTableBaseSearchSchema.extend({
  be_slug: z.string().optional(),
  client_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resource-invoices/",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    const permissionMethods = createRolePermissionMethods(
      context.appRole,
      context.rolePermissions,
    )

    if (permissionMethods?.getIsResourceSystemUser()) {
      throw redirect({
        to: "/my-invoices",
      })
    }
  },
  loaderDeps: ({ search }) =>
    pick(search, [
      "be_slug",
      "client_slug",
      "q",
      "page",
      "page_size",
      "sorting",
      "columnFilters",
    ]),
  loader: async ({ context, deps }) => {
    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })

    const filters = pick(deps, [
      "q",
      "page",
      "page_size",
      "sorting",
      "columnFilters",
    ])

    // Validate status filter - only allow admin-viewable statuses
    const existingStatusFilter = filters.columnFilters?.find(
      (f) => f.id === "status",
    )
    const isValidStatusFilter =
      existingStatusFilter &&
      (typeof existingStatusFilter.value === "string"
        ? ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES.includes(
            existingStatusFilter.value as (typeof ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES)[number],
          )
        : Array.isArray(existingStatusFilter.value) &&
          existingStatusFilter.value.every((v: string) =>
            ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES.includes(
              v as (typeof ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES)[number],
            ),
          ))

    // Only set default status filter if user hasn't selected a valid one
    if (!isValidStatusFilter) {
      filters.columnFilters = ensureColumnFilter(filters.columnFilters, {
        id: "status",
        value: ADMIN_VIEWABLE_RESOURCE_INVOICE_STATUSES,
      })
    }

    context.queryClient
      .ensureQueryData(getExtendedResourceInvoicesQueryOptions({ filters }))
      .catch((error) => {
        log.error("Error getting extended resource invoices", { error })
        return null
      })

    return {
      filters,
    }
  },
})

function RouteComponent() {
  const { isResourceInvoiceManagementOn } = useFeatureConfigs()

  if (!isResourceInvoiceManagementOn) {
    throw notFound()
  }

  const router = useRouter()
  const isMobile = useIsMobile()
  const { filters } = Route.useLoaderData()
  const { be_slug, client_slug } = Route.useSearch()
  const [exportDialogOpen, setExportDialogOpen] = useState(false)

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )

  const selectedBusinessEntity = useMemo(() => {
    const selected = businessEntitiesData.businessEntities.find(
      (entity) => entity.slug === be_slug,
    )

    if (selected) {
      return selected
    }

    return null
  }, [businessEntitiesData, be_slug])

  const businessEntityFilterOptions = useMemo(() => {
    return businessEntitiesData.businessEntities.map((entity) => ({
      label: entity.name,
      value: entity.slug,
    }))
  }, [businessEntitiesData])

  const { data: clientsData, isLoading: isClientsLoading } = useQuery(
    getExtendedClientsQueryOptions({
      filters: {
        page: 1,
        page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
        columnFilters: selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : undefined,
      },
    }),
  )

  const selectedClient = useMemo(() => {
    const selected = clientsData?.clients.find(
      (client) => client.slug === client_slug,
    )

    if (selected) {
      return selected
    }

    return null
  }, [clientsData, client_slug])

  const resourceInvoicesFilters = useMemo(() => {
    return {
      ...filters,
      columnFilters: [
        ...(filters.columnFilters ?? []),
        ...(selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : []),
        ...(selectedClient?.unit_id
          ? [{ id: "client_unit_ids", value: [selectedClient.unit_id] }]
          : []),
      ],
    }
  }, [filters, selectedBusinessEntity, selectedClient])

  const { data: resourceInvoicesData } = useQuery(
    getExtendedResourceInvoicesQueryOptions({
      filters: resourceInvoicesFilters,
    }),
  )

  const onSelectInvoice = (invoice: ExtendedResourceInvoice) => {
    router.navigate({
      to: "/resource-invoices/$invoiceId",
      params: { invoiceId: invoice.id },
      search: (prev) => prev,
    })
  }

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        be_slug: businessEntitySlug ?? undefined,
        client_slug: undefined,
      }),
    })
  }

  const onSelectClient = (client: ExtendedClient | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        client_slug: client?.slug ?? undefined,
      }),
    })
  }

  const pagination: Pagination = {
    total: resourceInvoicesData?.count ?? 0,
    page: filters.page,
    page_size: filters.page_size,
  }

  return (
    <PagePanel title="Resource Invoices">
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile && selectedClient,
          })}
        >
          <ClientsSearchList
            clients={clientsData?.clients ?? []}
            selectedClientSlug={selectedClient?.slug ?? null}
            onSelectClient={onSelectClient}
            filterOptions={businessEntityFilterOptions}
            filterValue={selectedBusinessEntity?.slug ?? null}
            setFilterValue={onSelectBusinessEntity}
            filterPlaceholder="Select a business entity"
            isLoading={isClientsLoading}
          />
        </div>

        <div
          className={cn("block flex-1 md:w-2/3 lg:w-3/4", {
            hidden: isMobile && !selectedClient,
          })}
        >
          <div className="h-full flex-1 overflow-auto">
            <PageDetailsPanel
              title={
                selectedClient
                  ? `${selectedClient.name} Resource Invoices`
                  : "Resource Invoices"
              }
            >
              <If
                condition={resourceInvoicesData}
                fallback={
                  <div className="h-full flex-1 overflow-auto">
                    <div className="flex h-64 items-center justify-center">
                      <div className="text-white/70">Loading invoices...</div>
                    </div>
                  </div>
                }
              >
                {(data) => (
                  <ResourceInvoicesTable
                    invoices={data.invoices}
                    enableStatusActions
                    onViewInvoice={onSelectInvoice}
                    onSelectInvoice={onSelectInvoice}
                    selectedClient={selectedClient}
                    pagination={pagination}
                    initialColumnFilters={filters.columnFilters}
                    initialSorting={filters.sorting}
                    onExportClick={() => setExportDialogOpen(true)}
                  />
                )}
              </If>
            </PageDetailsPanel>
          </div>
        </div>
      </div>
      <ExportInvoicesDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        selectedBusinessEntity={selectedBusinessEntity}
      />
    </PagePanel>
  )
}
